import React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { FullShoppingCartItem, IAltCheckoutPaymentMethod, ProductsResponse } from '../../utils/CommonTypes/StoreTypes';
import { ServerResponse } from '../../utils/Server';
import { formatMoney } from '../../utils/Tools';
import Icon, { IconType } from '../Icon/Icon';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import Modal from '../Modal/Modal';
import Payment from './Payment';
import { useEffect } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { IState, ReduxMap } from '../../redux/redux';
import { connect } from 'react-redux';
import Select from 'react-select';
import { ActionMeta, InputActionMeta } from 'react-select';
import { Selectable } from '../../utils/CommonTypes/CommonTypes';
import Button from '../_Core/Button';

interface ShoppingCartProps extends IState {
  cartContents: FullShoppingCartItem[];
  cartCount: number;
  couponsUsed: string[];
  fullProductResponse: ProductsResponse;
  handleProductsResponse: (stuff: ProductsResponse) => void;
  userId: string | null;
  orderId?: string;
  newUsersContinue: (reason: string, makeInvoice: boolean) => void;
  altCheckoutPaymentMethod?: IAltCheckoutPaymentMethod;
  refreshCart: (orderId?: string) => void;
  notRegistrationStore?: boolean;
}

interface IFeeOptions {
  payByCheck: boolean;
  payByCard: boolean;
  payByFeeWavier: boolean;
  payBySchool: boolean;
  generateInvoice: boolean;
}

interface IAltPaymentMethodForm {
  notes?: string;
  itemsToApplyCouponsTo: Selectable[];
}

interface IAltPaymentResponse {
  orderId: string;
  paid: boolean;
}

const ShoppingCart = (props: ShoppingCartProps) => {
  const [couponCode, setCouponCode] = useState('');
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [feeOptions, setFeeOptions] = useState<IFeeOptions>();
  const [form, setForm] = useState<IAltPaymentMethodForm>({ itemsToApplyCouponsTo: [] });
  const [productOptions, setOptions] = useState<Selectable[]>([]);
  const [cardTotal, setCardTotal] = useState<number>(0);

  useEffect(() => {
    if (props.Settings) {
      let options = props.fullProductResponse.registrationFeeOptions;

      var optionFee = {
        payByCheck: options.payByCheck && options.hasRegistrationFee,
        payByCard: props.Settings?.AcceptPayment == true,
        payByFeeWavier: options.payByFeeWavier && options.hasRegistrationFee,
        payBySchool: options.payBySchool && options.hasRegistrationFee,
        generateInvoice: options.generateInvoice,
      };

      if (props.notRegistrationStore) {
        optionFee.payByCheck = false;
        optionFee.payByFeeWavier = false;
        optionFee.payBySchool = false;
      }
      setStripePromise(loadStripe(props.Settings.stripePublicKey ?? ''));

      setFeeOptions(optionFee);
    }
  }, [props.Settings, props.fullProductResponse, props.notRegistrationStore]);

  useEffect(() => {
    //build select options
    let opts = [];
    if (props.fullProductResponse.products) {
      props.fullProductResponse.products
        .filter((x) => x.InBag && x.InBag.length > 0)
        .forEach((x) => {
          opts.push({ value: x.Id, label: x.Name });
        });
      setOptions(opts);
    }
  }, []);

  useEffect(() => {
    setCardTotal((props.fullProductResponse.total ?? 0) + (props.fullProductResponse.cardFee ?? 0));
  }, [props.fullProductResponse]);

  const saveAltPaymentMethod = () => {
    let request = {
      notes: form.notes,
      itemsToApplyCouponsTo: form.itemsToApplyCouponsTo.map((x) => x.value),
      userId: props.userId,
      orderId: props.orderId,
      newRecord: props.fullProductResponse.NewRecord,
    };

    if (request.itemsToApplyCouponsTo.length === 0) {
      toast.error('You must select at least one item to apply the payment to');
      return;
    }
    store.server.postApi<ServerResponse<IAltPaymentResponse>>('../Store/SaveAltPaymentMethod', request).then((x) => {
      if (x.Success) {
        if (x.Value.paid) {
          handlePayBy(props.altCheckoutPaymentMethod?.Name ?? 'Alt Payment Method');
        } else {
          props.refreshCart(x.Value.orderId);
        }
        setPaymentModalOpen(false);
      } else {
        toast.error(x.Message);
      }
    });

    toast.success('Payment Method Saved');
  };

  const handleCheckoutAndPay = () => {
    setPaymentModalOpen(true);

    let submitObj = {};
    // store.server.postApi('../Store/ProcessPayment')
  };

  const handleClearCart = () => {
    store.server
      .postApi<ServerResponse<ProductsResponse>>('../Store/Clear', {
        userId: props.userId,
        orderId: props.orderId,
      })
      .then((res) => {
        toast.success('cart cleared!');
        props.handleProductsResponse(res.Value);
      });
  };

  const handleRemoveItemClick = (item: FullShoppingCartItem) => {
    if (window.confirm(`Are you sure you want to remove ${item.Name} from the cart?`)) {
      let submitObj = {
        productId: item.Id,
        size: item.Size,
        userId: props.userId,
        orderId: props.orderId,
        custom: item.Custom,
        idToRemove: item.Id,
      };
      store.server.postApi<ServerResponse<ProductsResponse>>('../Store/RemoveItem', submitObj).then((res: any) => {
        toast.success('item removed');
        props.handleProductsResponse(res.Value);
      });
    }
  };

  const handleCouponSubmit = () => {
    store.server
      .postApi<ServerResponse<ProductsResponse>>('../Store/AddCoupon', {
        code: couponCode,
        userId: props.userId,
        orderId: props.orderId,
      })
      .then((res) => {
        if (!res.Success) {
          toast.warning(res.Message);
        } else {
          toast.success('coupon applied!');
          props.handleProductsResponse(res.Value);
          setCouponCode('');
        }
      });
  };

  const handlePayBy = (by: string) => {
    props.newUsersContinue(by, true);
  };

  const mappedCartContents = props.cartContents.map((item, i) => {
    let x = item.Custom && item.Custom.indexOf('{') == 0 ? JSON.parse(item.Custom) : item.Custom ? { Name: item.Custom } : {};
    return (
      <tr key={i}>
        <td>
          {item.Name} {x?.Name && <>- {x.Name}</>}
          {x?.OrgName && <>- {x.OrgName}</>}
          {x?.FirstName && (
            <>
              - {x.FirstName} {x.LastName ?? ''}
            </>
          )}
        </td>
        <td>{item.Size}</td>
        <td>{formatMoney.format(item.Free ? 0 : item.Cost)}</td>
        <td>{item.Count}</td>
        <td>{formatMoney.format((item.Free ? 0 : item.Cost) * item.Count)}</td>
        <td>
          {/* {item.Removeable && !x.DoNotAllowToBeRemoved && ( */}
          {item.Removeable && (
            <span className="text-danger click" title="remove" onClick={() => handleRemoveItemClick(item)}>
              <Icon type={IconType.trashO} />
            </span>
          )}
        </td>
      </tr>
    );
  });

  return (
    <div>
      <div className="flex-between">
        <h2>
          <Icon type={IconType.shoppingCart} /> Shopping Cart
        </h2>

        {StoreX.IsDev() && (
          <button className="btn btn-default" onClick={handleClearCart}>
            <Icon type={IconType.ban} /> Clear Cart
          </button>
        )}
      </div>
      <div>
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Cost</th>
              <th>Count</th>
              <th>Total</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {mappedCartContents}
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td>Savings: </td>
              <td>{formatMoney.format(props.fullProductResponse.savings ?? 0)}</td>
              <td></td>
            </tr>
            {props.fullProductResponse?.coupons && props.fullProductResponse.coupons.length > 0 && (
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  {props.fullProductResponse.coupons.map((x, i) => {
                    return (
                      <>
                        <span className="cart-coupon">{x}</span>{' '}
                      </>
                    );
                  })}
                </td>
                <td></td>
                <td></td>
              </tr>
            )}
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td>Cart Total: </td>
              <td>{formatMoney.format(props.fullProductResponse.total ?? 0)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-group">
        <div className="col-sm-4">
          <input className="form-control-coupon" placeholder="coupon code" type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} />
        </div>
        <button className="btn btn-secondary submit-coupon-btn" onClick={handleCouponSubmit}>
          <strong>Submit Coupon Code</strong>
        </button>
      </div>
      <div className="headroom-xl">
        {(props.fullProductResponse.total > 0 || props.fullProductResponse.cartCount > 0) && (
          <button className="btn btn-primary" type="button" onClick={handleCheckoutAndPay}>
            <Icon type={IconType.shoppingCart} /> {props.fullProductResponse.NewRecord ? <>{props.fullProductResponse.payContinueBtnText ?? 'Pay & continue'}</> : <>Checkout & Pay</>}
          </button>
        )}
        {!props.orderId && props.cartContents && props.cartContents.find((x) => x.Removeable) && (
          <button className="btn btn-default" type="button" onClick={handleClearCart}>
            <Icon type={IconType.ban} /> Clear Cart
          </button>
        )}
      </div>
      {paymentModalOpen && (
        <Modal title={props.Settings.text.CheckoutModal_Title || ''} setModalOpen={setPaymentModalOpen} size="l">
          <div dangerouslySetInnerHTML={{ __html: props.Settings.text.CheckoutModal_Header || '' }} />
          <div className="headroom-xl" />
          <Tabs>
            <TabList>
              {feeOptions?.payByCard && (
                <Tab>
                  <Icon type={IconType.creditCard} /> Card
                </Tab>
              )}
              {feeOptions.generateInvoice && (props.notRegistrationStore
                ? true
                : false) && (
                    <Tab>
                      <Icon type={IconType.fileInvoice} /> {props.fullProductResponse.generateInvoiceTabLabel}
                    </Tab>
                  )}
              {feeOptions?.payByCheck && (
                <Tab>
                  {/* <Icon type={IconType.checkMoney} /> Check */}
                  <div dangerouslySetInnerHTML={{ __html: props.Settings.text.CheckoutModal_CheckTabLabel || '' }} />
                </Tab>
              )}
              {feeOptions?.payBySchool && (
                <Tab>
                  <Icon type={IconType.school} /> School
                </Tab>
              )}
              {feeOptions?.payByFeeWavier && (
                <Tab>
                  <Icon type={IconType.stars} /> Fee Wavier <div dangerouslySetInnerHTML={{ __html: props.Settings.text.CheckoutModal_FeeWavierTabLabel }} />
                </Tab>
              )}
              {(props.altCheckoutPaymentMethod?.IsEnabled ?? false) === true && (props.notRegistrationStore ? false : true) && (
                <Tab>
                  <Icon type={IconType.trophy} />
                  <span dangerouslySetInnerHTML={{ __html: props.altCheckoutPaymentMethod.LabelTab ?? props.altCheckoutPaymentMethod.Name }} />
                </Tab>
              )}
            </TabList>
            {feeOptions?.payByCard && (
              <TabPanel>
                <Elements stripe={stripePromise}>
                  <Payment
                    paymentAmount={props.fullProductResponse.total ?? 0}
                    setModalOpen={setPaymentModalOpen}
                    fullProductResponse={props.fullProductResponse}
                    orderId={props.orderId}
                    cardFeeAmount={props.fullProductResponse.cardFee}
                    ifPaymentAmountIsZeroAutoProcess={true}
                  />
                </Elements>
              </TabPanel>
            )}

            {feeOptions.generateInvoice && (props.notRegistrationStore
              ? true
              : false) && (
                  <TabPanel>
                    <div>
                      <div>
                        <div dangerouslySetInnerHTML={{ __html: props.fullProductResponse.generateInvoiceHeader || '' }} />
                        <br />
                        <hr />
                        <br />
                        <button
                          className="btn btn-success"
                          type="button"
                          onClick={() => {
                            handlePayBy('Invoice');
                          }}>
                          <Icon type={IconType.fileInvoice} /> Generate Invoice
                        </button>
                      </div>
                    </div>
                  </TabPanel>
                )}
            {feeOptions?.payByCheck && (
              <TabPanel>
                <div>
                  <div dangerouslySetInnerHTML={{ __html: props.Settings.text.CheckoutModal_CheckTabHeader || '' }} />
                  <div>{props.fullProductResponse.registrationFeeOptions.checkInstructions}</div>
                  <br />
                  <hr />
                  <br />
                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={() => {
                      handlePayBy('Check');
                    }}>
                    <Icon type={IconType.checkSquareO} /> I agree & continue
                  </button>
                </div>
              </TabPanel>
            )}
            {feeOptions?.payBySchool && (
              <TabPanel>
                <div>
                  <div>
                    My school is paying for all of the registration fees. I understand that it is still my responsibility to pay my registration fee if my school does not. I understand that if my fee
                    is not paid I may not be able to compete.{' '}
                  </div>
                  <hr />
                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={() => {
                      handlePayBy('School');
                    }}>
                    <Icon type={IconType.checkSquareO} /> I agree & continue
                  </button>
                </div>
              </TabPanel>
            )}
            {feeOptions?.payByFeeWavier && (
              <TabPanel>
                <div>
                  <div>
                    I qualify for a fee waiver. I understand that this status will be checked and if I do not have or qualify for a fee waiver. I will be required to make payment by the date of the
                    fair.{' '}
                  </div>
                  <hr />
                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={() => {
                      handlePayBy('Wavier');
                    }}>
                    <Icon type={IconType.checkSquareO} /> I agree & continue
                  </button>
                </div>
              </TabPanel>
            )}
            {props.altCheckoutPaymentMethod?.IsEnabled === true && (props.notRegistrationStore
              ? false
              : true) && (
                  <TabPanel>
                    <div className="form-horizontal">
                      {props.altCheckoutPaymentMethod.DescriptionHtml && <div dangerouslySetInnerHTML={{ __html: props.altCheckoutPaymentMethod.DescriptionHtml }} />}

                      <div className="col-sm-12">
                        <div className="form-group">
                          <label className="control-label" htmlFor="itemsToApplyCouponsTo">
                            {props.altCheckoutPaymentMethod.LabelApplyToList}
                          </label>
                          <Select
                            value={form.itemsToApplyCouponsTo}
                            onChange={(x: any) => {
                              setForm({ ...form, itemsToApplyCouponsTo: x });
                            }}
                            options={productOptions}
                            isMulti={true}
                            id="itemsToApplyCouponsTo"
                          />
                        </div>
                        <div className="form-group">
                          <label className="control-label" htmlFor="notes">
                            {props.altCheckoutPaymentMethod.LabelNotes}
                          </label>
                          <textarea className="form-control" id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}></textarea>
                        </div>
                        <div className="headroom-xl" />
                        <div className="form-group ">
                          <Button type="save" text={props.altCheckoutPaymentMethod.LabelContinueBtn} onClick={saveAltPaymentMethod} />
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                )}
          </Tabs>
        </Modal>
      )}
    </div>
  );
};

//export default ReduxMap(ShoppingCart);

const mapStateToProps = (state) => {
  return { ...state.main };
};

export default connect(mapStateToProps)(ShoppingCart);
